import { useEffect, useState } from 'react';
import { useDebounceCallback } from '@react-hook/debounce';
import mockData from '../mock/groupDashboard.mock';
import {
  convertMetricsNameToV3,
  getDashboardMetricsV3
} from 'common/services/metrics.service';
import useGroupsTeam from '../useGroupTeams/useGroupTeams';
import useMetricsCalculationConfig from 'views/root/views/home/hooks/useMetricsCalculationConfig';
import useToast from 'components/hooks/useToast';
import { noop, keyBy } from 'lodash';

const useGroupMetricsNew = ({
  dates,
  roll_up = 'custom',
  setMetrics,
  managedPages,
  metricsNames,
  showToastOnError = false,
  setIsExpanding = noop
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const metricsCalculationConfig = useMetricsCalculationConfig();
  const { metricsCalculationStrategy } = metricsCalculationConfig;
  const { selectedGroupId, selectedGroupChildrenIds } = useGroupsTeam();
  const toast = useToast();

  const isMockEnv = false;

  const mockValues = roll_up => {
    if (roll_up === 'custom') {
      return mockData.groupMetrics;
    }
    return mockData.metricsDataOneDayRollUp;
  };

  const fetchGroupsMetrics = async teamIds => {
    const res = isMockEnv
      ? mockValues(roll_up)
      : await getDashboardMetricsV3({
          time_ranges: [{ after: dates?.from, before: dates?.to }],
          requested_metrics: convertMetricsNameToV3(
            metricsNames,
            metricsCalculationStrategy
          ),
          teamId: teamIds,
          offset: 0,
          return_no_data: true,
          roll_up
        });

    if (!res.length && showToastOnError) {
      toast({
        variant: 'error',
        message: 'ERROR',
        moreInfo: 'Oops. Something went wrong.'
      });
    } else {
      setMetrics?.(currMetrics => {
        const currMetricsByGroupId = keyBy(currMetrics, 'group');
        const resByGroupId = keyBy(res, 'group');
        const unifiedByGroup = { ...currMetricsByGroupId, ...resByGroupId };
        return Object.values(unifiedByGroup);
      });
    }

    setIsLoading(false);
    setIsExpanding(false);
  };

  const debouncedFetchGroupsMetrics = useDebounceCallback(
    teamIds => fetchGroupsMetrics(teamIds),
    800
  );

  useEffect(() => {
    managedPages.setManagedPages({});
    setMetrics?.([]);
    // init page of selected group in top bar
    managedPages.getPage(selectedGroupId);
    const teamIdsToFetch = selectedGroupChildrenIds.slice(
      0,
      managedPages.pageSize
    );

    if (!dates) {
      return;
    }

    setIsLoading(true);
    debouncedFetchGroupsMetrics(teamIdsToFetch);
  }, [dates?.from, dates?.to, selectedGroupId, metricsNames]);

  return {
    isLoading,
    debouncedFetchGroupsMetrics
  };
};

export default useGroupMetricsNew;
